import React, { useEffect, useState } from 'react';
import {
  getAllBuilderCategories,
  getSingleCommonLiveTask,
  getSingleIndependentLiveTask,
  updateLiveTask,
  getSingleGroupLiveTask,
  getAllItemsByProjectAndCategoryID
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import editIcon from '../../shared/assets/svg/pencil.svg';

const SingleTaskDetails = ({ apiType, taskID, isDrawerOpen, callApi }) => {
  const userId = Cookies.get('userId');
  const UserID = Cookies.get('userId');
  const {
    data: Task,
    refetch: refetchCommonLiveTask,
    isFetching
  } = useQuery({
    queryKey: ['getLiveTask', taskID],
    queryFn: () => {
      if (!taskID) {
        return Promise.resolve([]);
      }
      return apiType === 'common'
        ? getSingleCommonLiveTask(taskID)
        : apiType === 'independent'
          ? getSingleIndependentLiveTask(taskID)
          : apiType === 'group'
            ? getSingleGroupLiveTask(taskID)
            : Promise.resolve([]);
    },
    enabled: !!taskID
  });

  const { data: AllCategory, refetch: refetchCategories } = useQuery({
    queryKey: ['getCategory', UserID],
    queryFn: () => {
      if (!UserID) {
        return Promise.resolve([]); // protect against undefined values
      }
      return getAllBuilderCategories(UserID);
    },
    enabled: !!UserID
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Single Task Details Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchCategories();
  }, []);

  const colors = ['bg-orange-400', 'bg-blue-500', 'bg-green-500', 'bg-purple-500'];
  const [materialCategories, setMaterialCategories] = useState([
    {
      id: Date.now(),
      items: [{ id: Date.now(), item_id: '', quantity: '' }]
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define selectedProjectId early to avoid reference errors
  const selectedProjectId = Task?.data?.project_id;

  // Function to fetch items by project and category
  const getItemsByProjectAndCategory = async (projectId, categoryId) => {
    if (!projectId || !categoryId) return [];
    try {
      return await getAllItemsByProjectAndCategoryID(projectId, categoryId);
    } catch (error) {
      console.error('Error fetching items:', error);
      return [];
    }
  };
  const [editingBudget, setEditingBudget] = useState(false);
  const [newBudget, setNewBudget] = useState('');
  const [editingMaterialBudget, setEditingMaterialBudget] = useState(false);
  const [materialBudget, setMaterialBudget] = useState('');
  const [editingMaterialIndex, setEditingMaterialIndex] = useState(null);
  const [editingMaterialData, setEditingMaterialData] = useState(null);

  const { mutate: updateTask } = useMutation({
    mutationFn: ({ id, payload }) => updateLiveTask(id, payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Task updated successfully');
      setMaterialCategories([
        {
          id: Date.now(),
          items: [{ id: Date.now(), item_id: '', quantity: '' }]
        }
      ]);
      // Reset editing state
      setEditingMaterialIndex(null);
      setEditingMaterialData(null);
      await refetchCommonLiveTask();
      await callApi?.();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error updating task', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating task');
    }
  });

  const formatDate = (date) => {
    if (!date) return '-';
    const options = { day: 'numeric', month: 'short' };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  const handleAddCategory = () => {
    setMaterialCategories([
      ...materialCategories,
      {
        id: Date.now() + Math.random(),
        items: [{ id: Date.now() + Math.random(), item_id: '', quantity: '' }]
      }
    ]);
  };

  const handleAddItem = (categoryId) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: [
                ...category.items,
                { id: Date.now() + Math.random(), item_id: '', quantity: '' }
              ]
            }
          : category
      )
    );
  };

  const handleRemoveItem = (categoryId, itemId) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.filter((item) => item.id !== itemId)
            }
          : category
      )
    );
  };

  const handleRemoveCategory = (categoryId) => {
    setMaterialCategories((prev) => prev.filter((category) => category.id !== categoryId));
  };

  const handleMaterialBudgetSave = () => {
    const id = Task?.data?.id;
    if (id && materialBudget) {
      const payload = {
        material_budget: Number(materialBudget)
      };
      updateTask({ id, payload });
      setEditingMaterialBudget(false);
    }
  };

  const handleLabourBudgetSave = () => {
    const id = Task?.data?.id;
    if (id && newBudget) {
      const payload = {
        labour_budget: Number(newBudget)
      };
      updateTask({ id, payload });
      setEditingBudget(false);
    }
  };

  useEffect(() => {
    if (isDrawerOpen) {
      // Drawer is closed, reset all local states
      setMaterialCategories([
        {
          id: Date.now(),
          items: [{ id: Date.now(), item_id: '', quantity: '' }]
        }
      ]);
      setIsSubmitting(false);
      setEditingBudget(false);
      setEditingMaterialBudget(false);
      setNewBudget('');
      setMaterialBudget('');
      setEditingMaterialIndex(null);
      setEditingMaterialData(null);
    }
  }, [isDrawerOpen]);

  const handleCategoryChange = async (categoryId, selectedCategoryId) => {
    // First update the category immediately for UI feedback
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              category_id: selectedCategoryId,
              available_items: [], // Clear items initially
              items: category.items.map((item) => ({
                ...item,
                category_id: selectedCategoryId,
                item_id: '', // Reset item when category changes
                quantity: ''
              }))
            }
          : category
      )
    );

    // Fetch items for the selected category and project
    let categoryItems = [];
    if (selectedProjectId && selectedCategoryId) {
      try {
        categoryItems = await getItemsByProjectAndCategory(selectedProjectId, selectedCategoryId);

        // Map new API response to available_items for dropdown
        const mappedItems = (categoryItems || []).map((apiItem) => ({
          id: apiItem.id, // OUTER id (parent id)
          name: apiItem.item?.name,
          unit: apiItem.unit || apiItem.item?.units?.[0] || apiItem.item?.uom || 'unit'
        }));

        // Update with fetched items
        setMaterialCategories((prev) =>
          prev.map((category) =>
            category.id === categoryId
              ? {
                  ...category,
                  available_items: mappedItems
                }
              : category
          )
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to fetch items for selected category'
        );
      }
    } else {
      if (!selectedProjectId) {
        toast.warning('Project ID not available from task data');
      }
    }
  };

  const handleItemChange = (categoryId, itemId, selectedItemInfo) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      item_id: selectedItemInfo.id,
                      item_name: selectedItemInfo.name,
                      unit: selectedItemInfo.unit
                    }
                  : item
              )
            }
          : category
      )
    );
  };

  const handleQuantityChange = (categoryId, itemId, quantity) => {
    setMaterialCategories((prev) =>
      prev.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              items: category.items.map((item) =>
                item.id === itemId
                  ? {
                      ...item,
                      quantity: quantity
                    }
                  : item
              )
            }
          : category
      )
    );
  };

  const handleAddUpdateMaterials = () => {
    const newMaterials = [];

    for (const category of materialCategories) {
      for (const item of category.items) {
        const hasAnyValue = item.item_id || item.quantity;

        // If any value is provided, require all fields
        if (hasAnyValue) {
          if (!item.item_id || !item.quantity) {
            toast.error('If you select an item, quantity must be provided.');
            return;
          }

          // Get the unit from the selected item
          const selectedUnit = item.unit || 'unit';

          newMaterials.push({
            item_id: Number(item.item_id),
            unit: selectedUnit,
            quantity: Number(item.quantity)
          });
        }
      }
    }

    // Get existing materials from the current task
    const existingMaterials =
      Task?.data?.materialTaskMapping?.map((material) => ({
        item_id: material.item_id,
        unit: material.unit,
        quantity: material.quantity
      })) || [];

    // Combine existing materials with new materials
    const allMaterials = [...existingMaterials, ...newMaterials];

    const id = Task?.data?.id;
    if (id && (newMaterials.length > 0 || existingMaterials.length > 0)) {
      const payload = {
        materials: allMaterials
      };
      console.log('Sending materials payload:', payload);
      updateTask({ id, payload });
    }
  };

  const handleEditMaterial = async (materialIndex, material) => {
    setEditingMaterialIndex(materialIndex);
    setEditingMaterialData(material);

    console.log('material', material);

    // Reset the form and set up for editing
    const categoryId = material.item?.category?.id;
    if (categoryId && selectedProjectId) {
      try {
        // Fetch items for this category
        let categoryItems = await getItemsByProjectAndCategory(selectedProjectId, categoryId);
        let mappedItems = (categoryItems || []).map((apiItem) => ({
          id: apiItem.id, // OUTER id (parent id)
          name: apiItem.item?.name,
          unit: apiItem.unit || apiItem.item?.units?.[0] || apiItem.item?.uom || 'unit'
        }));

        // Ensure the current item is present in available_items
        if (!mappedItems.some((item) => String(item.id) === String(material.item_id))) {
          mappedItems = [
            ...mappedItems,
            {
              id: String(material.item_id),
              name: material.item?.name,
              unit: material.unit
            }
          ];
        }

        setMaterialCategories([
          {
            id: Date.now(),
            category_id: categoryId,
            available_items: mappedItems,
            items: [
              {
                id: Date.now(),
                item_id: String(material.item_id),
                item_name: material.item?.name,
                unit: material.unit,
                quantity: material.quantity,
                category_id: categoryId
              }
            ]
          }
        ]);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.message || 'Failed to load material for editing'
        );
      }
    }
  };

  const handleUpdateMaterial = () => {
    const updatedMaterial = materialCategories[0]?.items[0];

    if (!updatedMaterial?.item_id || !updatedMaterial?.quantity) {
      toast.error('Please fill all required fields.');
      return;
    }

    // Get existing materials
    const existingMaterials =
      Task?.data?.materialTaskMapping?.map((material, index) => {
        if (index === editingMaterialIndex) {
          // Replace with updated material
          return {
            item_id: Number(updatedMaterial.item_id),
            unit: updatedMaterial.unit || 'unit',
            quantity: Number(updatedMaterial.quantity)
          };
        }
        // Keep existing material as is
        return {
          item_id: material.item_id,
          unit: material.unit,
          quantity: material.quantity
        };
      }) || [];

    const id = Task?.data?.id;
    if (id) {
      const payload = {
        materials: existingMaterials
      };
      console.log('Updating material payload:', payload);
      updateTask({ id, payload });
      handleCancelEdit();
    }
  };

  const handleCancelEdit = () => {
    setEditingMaterialIndex(null);
    setEditingMaterialData(null);
    // Reset form
    setMaterialCategories([
      {
        id: Date.now(),
        items: [{ id: Date.now(), item_id: '', quantity: '' }]
      }
    ]);
  };

  const handlePrepareTableData = () => {
    const tableData = Task?.data?.materialTaskMapping.map((material) => ({
      category: material.item?.category?.name ? material.item.category.name.slice(0, 20) : 'N/A',

      item: material.item?.item_name || 'N/A',
      unit: material.unit || 'N/A',
      quantity: material.quantity ?? 'N/A' // Use ?? to allow 0 quantity
    }));

    return tableData;
  };

  if (isFetching) {
    return (
      <div className="flex justify-center items-center h-64 text-gray-600">Loading updates...</div>
    );
  }

  if (!Task) return <div>No Task Selected</div>;

  return (
    <div className="p-6 bg-white rounded-md shadow-md">
      <div className="grid grid-cols-2 gap-4 text-sm mb-6">
        <div>
          <p className="font-semibold">
            Planned Date - {formatDate(Task?.data?.planned_start_date)}
          </p>
        </div>
        <div>
          <p className="font-semibold">End Date - {formatDate(Task?.data?.planned_end_date)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm mb-6">
        <div>
          <p className="font-semibold mb-2">Assigned Employee Names</p>
          <div className="flex flex-col gap-2">
            {Task?.data?.engineerLiveTaskMapping?.map((eng, index) => (
              <div key={eng.id} className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full ${colors[index % colors.length]}  text-white text-xs flex items-center justify-center`}>
                  {eng?.user?.name?.charAt(0).toUpperCase()}
                </div>
                {eng?.user?.name}
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="font-semibold mb-2">Assigned Contractor Names</p>
          <div className="flex flex-col gap-2">
            {Task?.data?.spLiveTaskMapping?.map(
              (sp, index) =>
                sp?.user?.serviceProvider?.category_id === 1 && (
                  <div key={sp.id} className="flex items-center gap-2">
                    <div
                      className={`w-6 h-6 rounded-full ${colors[index % colors.length]}  text-white text-xs flex items-center justify-center`}>
                      {sp?.user?.name?.charAt(0).toUpperCase()}
                    </div>
                    {sp?.user?.name}
                  </div>
                )
            )}
          </div>
        </div>
      </div>

      <div className="text-sm mb-6">
        <p className="font-semibold mb-2">Assigned Architect Names</p>
        <div className="flex flex-row gap-2 flex-wrap justify-between">
          {Task?.data?.architectLiveTaskMapping?.map(
            (sp, index) =>
              sp?.user?.serviceProvider?.category_id === 2 && (
                <div key={sp.id} className="flex items-center gap-2 w-fit">
                  <div
                    className={`w-6 h-6 rounded-full ${colors[index % colors.length]}  text-white text-xs flex items-center justify-center`}>
                    {sp?.user?.name?.charAt(0).toUpperCase()}
                  </div>
                  {sp?.user?.name}
                </div>
              )
          )}
        </div>
      </div>
      {Task?.data?.subActivity.length > 0 && (
        <div className="text-sm mb-6">
          <p className="font-semibold mb-2">Sub activities list</p>
          <ul className="list-disc list-inside">
            {Task?.data?.subActivity.map((t, index) => (
              <li key={index}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-sm space-y-2 mb-6">
        <p>
          Idle Time - {Task?.data?.delay}
          {Task?.data?.delay === 1 ? 'Day' : 'Days'}
        </p>
        <p>
          Duration - {Task?.data?.duration} {Task?.data?.duration === 1 ? 'Day' : 'Days'}
        </p>

        {Task?.data?.dependencies?.length > 0 && (
          <p>
            Dependency Info -{' '}
            {Task?.data?.dependencies?.length
              ? Task.data.dependencies.map((dep) => dep.dependency?.name).join(', ')
              : '-'}
          </p>
        )}

        <p>Drawing Required - {Task?.data?.drawing_required ? 'Yes' : 'No'}</p>

        <div className="flex items-center gap-2">
          <p>Labour Budget -</p>

          {editingBudget ? (
            <>
              <input
                type="number"
                placeholder="Enter budget"
                value={newBudget}
                onChange={(e) => setNewBudget(e.target.value)}
                className="border p-1 rounded text-sm w-30"
              />
              <button
                onClick={handleLabourBudgetSave}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                disabled={isSubmitting}>
                Save
              </button>
              <button
                onClick={() => setEditingBudget(false)}
                className="text-blue-500 underline text-xs">
                cancel
              </button>
            </>
          ) : (
            <>
              {Task?.data?.labour_budget ? (
                <div className="flex items-center gap-2">
                  <span>{Task?.data?.labour_budget}</span>
                  <button
                    onClick={() => {
                      setNewBudget(Task?.data?.labour_budget); // Set existing value into input
                      setEditingBudget(true); // Open input box
                    }}
                    className="text-gray-500 hover:text-gray-700">
                    ✏️
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingBudget(true)}
                  className="text-blue-500 underline text-xs"
                  disabled={isSubmitting}>
                  Add
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <p>Material Budget -</p>

          {editingMaterialBudget ? (
            <>
              <input
                type="number"
                placeholder="Enter budget"
                value={materialBudget}
                onChange={(e) => setMaterialBudget(e.target.value)}
                className="border p-1 rounded text-sm w-30"
              />
              <button
                onClick={handleMaterialBudgetSave}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                disabled={isSubmitting}>
                Save
              </button>
              <button
                onClick={() => setEditingMaterialBudget(false)}
                className="text-blue-500 underline text-xs">
                cancel
              </button>
            </>
          ) : (
            <>
              {Task?.data?.material_budget ? (
                <div className="flex items-center gap-2">
                  <span>{Task?.data?.material_budget}</span>
                  <button
                    onClick={() => {
                      setMaterialBudget(Task?.data?.material_budget); // Set existing value into input
                      setEditingMaterialBudget(true); // Open input box
                    }}
                    className="text-gray-500 hover:text-gray-700">
                    ✏️
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setEditingMaterialBudget(true)}
                  className="text-blue-500 underline text-xs"
                  disabled={isSubmitting}>
                  Add
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 
      <div className="flex justify-start space-x-4 mt-6">
        <button
          className="px-4 py-2 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
          disabled={isSubmitting}
          onClick={editingMaterialIndex !== null ? handleUpdateMaterial : handleAddUpdateMaterials}>
          {editingMaterialIndex !== null ? 'Update Material' : 'Add Materials'}
        </button>
        {editingMaterialIndex !== null && (
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded-md text-sm hover:bg-gray-600"
            onClick={handleCancelEdit}>
            Cancel
          </button>
        )}
      </div> */}
    </div>
  );
};

export default SingleTaskDetails;
